import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteTicket, fetchTickets, updateTicket } from '../../api/ticketsModule';
import RoleGuard from '../../components/RoleGuard';
import { useToast } from '../../components/ToastContext';
import useModuleAuth from '../../hooks/useModuleAuth';

const statuses = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const priorities = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600"
        >
          Close
        </button>
      </div>
      <div className="pt-4">{children}</div>
    </div>
  </div>
);

const TicketsPage = () => {
  const { isAdmin } = useModuleAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [page, setPage] = useState(1);
  const [activeTicket, setActiveTicket] = useState(null);
  const [editTicket, setEditTicket] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', filters, page],
    queryFn: () => fetchTickets({ ...filters, page, limit: 10 }),
    enabled: isAdmin,
  });

  const items = data?.data || [];
  const meta = data?.meta || {};

  useEffect(() => {
    setPage(1);
  }, [filters.status, filters.priority, filters.search]);

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateTicket(id, payload),
    onSuccess: () => {
      toast.success('Ticket updated');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setEditTicket(null);
    },
    onError: (err) => toast.error(err?.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTicket,
    onSuccess: () => {
      toast.success('Ticket deleted');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err) => toast.error(err?.message || 'Delete failed'),
  });

  if (!isAdmin) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <p className="text-sm text-slate-600">Only admins can view global support tickets.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-700">Customers</p>
            <h1 className="text-xl font-bold text-slate-900">Support tickets</h1>
            <p className="text-sm text-slate-600">Track customer requests and resolutions.</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <input
            type="search"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Search subject or customer"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none md:max-w-sm"
          />
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <select
            value={filters.priority}
            onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All priorities</option>
            {priorities.map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        {error && (
          <div className="mb-4 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error.message || 'Failed to load tickets'}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    No tickets found.
                  </td>
                </tr>
              )}
              {items.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{ticket.subject}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {ticket.customer?.firstName} {ticket.customer?.lastName}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="rounded-full border border-slate-200 px-2 py-1 font-semibold text-slate-600">
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="rounded-full border border-slate-200 px-2 py-1 font-semibold text-slate-600">
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{ticket.assignedTo || '-'}</td>
                  <td className="px-4 py-3">
                    <RoleGuard>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveTicket(ticket)}
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditTicket(ticket)}
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Delete this ticket?')) {
                              deleteMutation.mutate(ticket.id);
                            }
                          }}
                          className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </RoleGuard>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <div>
            Page {meta.page || 1} of {Math.max(1, meta.totalPages || 1)}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={(meta.page || 1) <= 1}
              className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={(meta.page || 1) >= (meta.totalPages || 1)}
              className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {activeTicket && (
        <Modal title="Ticket details" onClose={() => setActiveTicket(null)}>
          <div className="space-y-2 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">{activeTicket.subject}</p>
            <p>{activeTicket.message}</p>
            <p>
              Customer: {activeTicket.customer?.firstName} {activeTicket.customer?.lastName}
            </p>
          </div>
        </Modal>
      )}

      {editTicket && (
        <Modal title="Update ticket" onClose={() => setEditTicket(null)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              updateMutation.mutate({
                id: editTicket.id,
                payload: {
                  status: formData.get('status'),
                  priority: formData.get('priority'),
                  assignedTo: formData.get('assignedTo') || null,
                },
              });
            }}
            className="space-y-3"
          >
            <select
              name="status"
              defaultValue={editTicket.status}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <select
              name="priority"
              defaultValue={editTicket.priority}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {priorities.map((priority) => (
                <option key={priority} value={priority}>
                  {priority}
                </option>
              ))}
            </select>
            <input
              name="assignedTo"
              defaultValue={editTicket.assignedTo || ''}
              placeholder="Assigned to"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default TicketsPage;
